import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Building, Locked } from '@mint-lib/icons';
import {
  InputLabel,
  InputRoot,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  typographyVariants,
} from '@mint-lib/tw-components';

import { toUserName } from '../../../helpers/knowledge-base/userName.js';
import type {
  FolderAccess,
  FolderAccessDraft,
} from '../../../hooks/knowledge-base/useFolderAccessDraft.js';
import type { FolderAccessUsers } from '../../../hooks/knowledge-base/useFolderAccessUsers.js';
import { InvitedUser } from './InvitedUser.jsx';
import { UsersPicker } from './UsersPicker.jsx';

type Props = {
  draft: FolderAccessDraft;
  users: FolderAccessUsers;
};

export const FolderAccessFields = ({ draft, users }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const { access, ids, setAccess, add, remove } = draft;
  const {
    me,
    ownerId,
    workspaceName,
    workspaceUsers,
    isLoadingUsers,
    resolveUser,
  } = users;

  // Me and the owner are pinned: neither is picked or removed here.
  const isPinned = (id: string) => id === me.id || id === ownerId;

  const owner = ownerId && ownerId !== me.id ? resolveUser(ownerId) : undefined;

  const isWorkspaceWide = access === 'workspace';

  const options = useMemo(
    () =>
      workspaceUsers
        .filter(
          ({ userId }) =>
            String(userId) !== me.id &&
            String(userId) !== ownerId &&
            !ids.includes(String(userId)),
        )
        .map((user) => ({
          label: toUserName(user),
          value: String(user.userId),
        })),
    [workspaceUsers, me.id, ownerId, ids],
  );

  const invitedUsers = ids.flatMap((id) => {
    const user = isPinned(id) ? undefined : resolveUser(id);

    return user ? [{ id, user }] : [];
  });

  return (
    <div className="flex flex-col gap-4">
      <p className={typographyVariants({ variant: 'h6' })}>
        {t('Share folder')}
      </p>

      <UsersPicker
        options={options}
        isLoading={isLoadingUsers}
        disabled={isWorkspaceWide}
        onAdd={add}
      />

      <InputRoot>
        <InputLabel
          htmlFor="shared-folder-access"
          label={t('Who has access')}
        />
        <Select
          value={access}
          onValueChange={(value) => setAccess(value as FolderAccess)}
        >
          <SelectTrigger
            id="shared-folder-access"
            size="medium"
            className="w-full"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="invited" startAdornment={<Locked />}>
              {t('Only those invited')}
            </SelectItem>
            <SelectItem value="workspace" startAdornment={<Building />}>
              {t('All {{workspace}} users', { workspace: workspaceName })}
            </SelectItem>
          </SelectContent>
        </Select>
      </InputRoot>

      {!isWorkspaceWide && (
        <ul className="flex flex-col" data-testid="invited-users">
          <InvitedUser
            firstName={me.firstName}
            lastName={me.lastName}
            label={t('{{name}} (you)', { name: toUserName(me) })}
          />
          {owner && (
            <InvitedUser
              firstName={owner.firstName}
              lastName={owner.lastName}
              label={t('{{name}} (owner)', {
                name: toUserName(owner) || owner.email || '',
              })}
            />
          )}
          {invitedUsers.map(({ id, user }) => (
            <InvitedUser
              key={id}
              firstName={user.firstName}
              lastName={user.lastName}
              label={toUserName(user) || user.email || ''}
              onRemove={() => remove(id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
